import { useMemo, useState, useEffect } from 'react';
import { PurchaseOrderItem, PurchaseOrderItemStatus } from '@domain/purchasing';


export type GroupByOption = 'purchaseOrder' | 'supplier' | 'shipment' | 'product';

export interface PurchaseOrderFilterCriteria {
  purchaseOrderId?: string;
  productId?: string;
  shipmentId?: string;
  supplierId?: string;
  availableDaysAgo?: number;
  includeDraftPurchaseOrders?: boolean;
}

const STORAGE_KEY = 'purchaseOrderFilterCriteria';

// Default filter criteria - filter out available items older than 32 days
const getDefaultCriteria = (): PurchaseOrderFilterCriteria => {
  return {
    availableDaysAgo: 28,
    includeDraftPurchaseOrders: false,
  };
};

/**
 * A hook for filtering purchase order items based on criteria
 * @param purchaseOrderItems The purchase order items to filter
 * @param initialCriteria Initial filter criteria (optional)
 * @returns Object containing filtered items, filter criteria, and methods to update filters
 */
export const usePurchaseOrderFilter = (
  purchaseOrderItems: PurchaseOrderItem[] = [],
  initialCriteria: PurchaseOrderFilterCriteria = {}
) => {
  // Load from localStorage or use default
  const [filterCriteria, setFilterCriteria] = useState<PurchaseOrderFilterCriteria>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with default criteria to ensure we have all required defaults
        return { ...getDefaultCriteria(), ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load purchase order filter criteria from localStorage:', error);
    }
    return { ...getDefaultCriteria(), ...initialCriteria };
  });

  // Save to localStorage whenever filter criteria changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filterCriteria));
    } catch (error) {
      console.warn('Failed to save purchase order filter criteria to localStorage:', error);
    }
  }, [filterCriteria]);

  const filteredItems = useMemo(() => {
    return purchaseOrderItems.filter(item => {
      // Purchase Order ID filter
      if (filterCriteria.purchaseOrderId && item.purchaseOrder?.id !== filterCriteria.purchaseOrderId) {
        return false;
      }

      // Product ID filter
      if (filterCriteria.productId && item.product.id !== filterCriteria.productId) {
        return false;
      }

      // Shipment ID filter
      if (filterCriteria.shipmentId && item.shipment?.id !== filterCriteria.shipmentId) {
        return false;
      }

      // Supplier filter (matches embedded PO supplier or supplier id)
      if (filterCriteria.supplierId) {
        const sid =
          item.purchaseOrder?.supplierId ?? item.purchaseOrder?.supplier?.id;
        if (sid !== filterCriteria.supplierId) {
          return false;
        }
      }

      // Include draft purchase orders filter
      if (filterCriteria.includeDraftPurchaseOrders === false && item.purchaseOrder?.draft === true) {
        return false;
      }

      // Available days ago filter (for AVAILABLE status items)
      if (filterCriteria.availableDaysAgo !== undefined && item.status === PurchaseOrderItemStatus.AVAILABLE) {
        const itemUpdatedDate = item.updatedAt;
        if (itemUpdatedDate) {
          const daysSinceUpdate = Math.floor((Date.now() - itemUpdatedDate.getTime()) / (1000 * 60 * 60 * 24));
          if (daysSinceUpdate > filterCriteria.availableDaysAgo) {
            return false;
          }
        }
      }

      return true;
    });
  }, [purchaseOrderItems, filterCriteria]);

  const updateFilter = (criteria: Partial<PurchaseOrderFilterCriteria>) => {
    setFilterCriteria(prev => ({
      ...prev,
      ...criteria
    }));
  };

  const clearFilters = () => {
    const defaultCriteria = getDefaultCriteria();
    setFilterCriteria(defaultCriteria);
  };

  const resetToDefault = () => {
    setFilterCriteria(getDefaultCriteria());
  };

  return {
    filteredItems,
    filterCriteria,
    updateFilter,
    clearFilters,
    resetToDefault,
    setFilterCriteria
  };
};
